<template>
  <div v-if="isLoading">
    <TransactionDetailSkeleton />
  </div>
  <Container v-else isSmallerGap>
    <template #header>
      <div class="transaction-detail-accordion_header" @click="toggle">
        <Text pattern="body1" :weight="600">
          {{ title || $t("TransactionDetailAccordion | Title | Details") }}
        </Text>
        <span class="arrow" :class="{ open: isOpen }">
          <BaseSvgIcon
            name="arrow-simple"
            class="transaction-detail-accordion_arrow-icon"
          />
        </span>
      </div>
    </template>
    <template #body>
      <BaseAccordionBody class="accordion" :show="isOpen" :transition="150">
        <div class="transaction-detail-accordion_body">
          <div class="transaction-detail-accordion_body_main">
            <div
              class="transaction-detail-accordion_body_item"
              v-for="{ id, label, value, underline, copy } in data"
              :key="id"
            >
              <Text pattern="body2" :weight="500" :height="150" color="#787878">
                {{ $t(label) }}
              </Text>
              <div class="transaction-detail-accordion_body_item_value">
                <div
                  v-if="label === 'Status'"
                  class="transaction-detail-accordion_body_item_value_titles"
                >
                  <Text
                    :class="
                      value.title === 'Confirmed'
                        ? 'confirmed-text'
                        : 'unconfirmed-text'
                    "
                    pattern="body2"
                    :weight="500"
                    :height="135"
                    :textDecoration="underline ? 'underline' : 'none'"
                    wordBreak="break-word"
                  >
                    {{ $t(value.title) }}
                  </Text>
                  <Text
                    class="blocks-text"
                    pattern="body2"
                    color="#7a7a7a"
                    :weight="500"
                    :height="135"
                    v-if="value.confirmationCount"
                  >
                    {{
                      value.title === "Confirmed"
                        ? $t("explorer.by_number_blocks", {
                            amount: value.confirmationCount,
                          })
                        : $t("explorer.transaction.unconfirmed_block_count", {
                            current: value.confirmationCount,
                            amount: value.totalConfirmations,
                          })
                    }}
                  </Text>
                </div>
                <div
                  v-if="label !== 'Status'"
                  class="transaction-detail-accordion_body_item_value_titles"
                >
                  <a
                    v-if="value.href"
                    :href="value.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="accordion-link link"
                  >
                    {{ shorten(value.title) }}
                  </a>
                  <Text
                    v-else-if="value.title"
                    pattern="body2"
                    :weight="500"
                    :height="135"
                    :textDecoration="underline ? 'underline' : 'none'"
                    wordBreak="break-word"
                  >
                    {{ $t(value.title) }}
                  </Text>
                  <Text
                    v-if="value.subtitle"
                    pattern="body2"
                    color="#7E7E7E"
                    :weight="500"
                    :height="135"
                  >
                    {{ $t(value.subtitle) }}
                  </Text>
                </div>
                <BaseSvgIcon
                  v-if="copy"
                  name="copy"
                  class="copy"
                  @click="copyToClipboard(value.title || value)"
                />
              </div>
            </div>
          </div>
        </div>
      </BaseAccordionBody>
    </template>
  </Container>
</template>

<script setup>
import { ref } from "vue";
import { Text, BaseSvgIcon, BaseAccordionBody, Container } from "@/shared/ui";
import { shorten } from "@/shared/utils/format";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/arrow-simple.svg";
import TransactionDetailSkeleton from "./TransactionDetailSkeleton.vue";

defineOptions({ name: "TransactionDetailAccordion" });

defineProps({
  title: {
    type: String,
    default: null,
  },
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const isOpen = ref(true);

function toggle() {
  isOpen.value = !isOpen.value;
}

function copyToClipboard(value) {
  navigator.clipboard.writeText(value);
}
</script>

<style lang="scss" scoped>
:deep(.container_body) {
  border: none;
}

.transaction-detail-accordion {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  background: var(--Dark-900, #111);
  padding: 32px;

  &_body {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    &_main {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;
      width: 100%;
      grid-column-gap: 32px;

      @media (width < 1024px) {
        grid-template-columns: 1fr;
      }
    }

    &_item {
      display: grid;
      grid-template-columns: 1fr 1.5fr;
      border-bottom: 1px solid #1a1a1a;
      padding: 16px 0;

      &:nth-last-child(-n + 2) {
        border-bottom: none;
      }

      &_value {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 6px;

        &_titles {
          display: flex;
          flex-direction: column;
          gap: 2px;
          word-break: break-word;

          .accordion-link {
            font-size: 14px;
            font-weight: 500;
            line-height: 1.35;
            //color: var(--Link-Color, #8f42ff);
            text-decoration: none;
            cursor: pointer;

            &:hover {
              text-decoration: underline;
            }
          }
        }

        .copy {
          min-width: 16px;
          min-height: 16px;
          transition: color 0.3s;
          cursor: pointer;
          color: #c18afd;

          &:hover {
            color: #fff;
          }
        }
      }
    }
  }

  &_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    width: 100%;
  }

  &_arrow-icon {
    width: 24px;
    height: 24px;
  }
}

.arrow {
  transition: transform 0.3s ease;
  margin-left: 16px;
}

.open {
  transform: rotate(180deg);
}

.container {
  :deep(.e-accordion-body) {
    display: flex;
    width: 100%;

    .e-accordion-body__body {
      display: flex;
      width: 100%;
    }
  }
}

.confirmed-text {
  color: #43a047 !important;
  line-height: 150% !important;
}

.unconfirmed-text {
  color: #e53935 !important;
  line-height: 150% !important;
}

.blocks-text {
  line-height: 150% !important;
}
</style>
